import { Icon, loadDomMax, type SvgIcon } from '@bridebook/aura';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { LazyMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { ButtonHTMLAttributes, forwardRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { useTabsContext } from './tabs-context';

const tabItemVariants = cva(
  'group relative inline-flex cursor-pointer items-center justify-center overflow-visible whitespace-nowrap disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: cn(
          // layout + spacing
          'gap-1 border-b transition-all',
          // colors + active states
          'border-space-15 text-space-60 hover:border-indigo-crush-40 data-[state=active]:text-space',
          // sizing + typography
          'h-[40px] rounded-t-lg typo-sm',
          // focus states
          'ring-0 ring-transparent outline-none ring-inset focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-0 focus-visible:outline-none',
        ),
        secondary: cn(
          // layout + spacing
          'gap-1.5 rounded-full border transition-all duration-150 ease-out',
          // colors + active states
          'border-space-15 bg-white text-space-75 hover:bg-space-04',
          'data-[state=active]:border-indigo-crush data-[state=active]:bg-indigo-crush-04 data-[state=active]:text-space',
          'data-[state=active]:hover:border-indigo-crush data-[state=active]:hover:bg-indigo-crush-10',
          // focus states
          'ring-inset focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-0 focus-visible:outline-none',
          // sizing + typography
          'h-[32px] px-3 typo-xs',
        ),
      },
    },
  },
);

const primaryTabItemVariants = cva('', {
  variants: {
    size: {
      responsive: 'px-4 xl:px-6',
      mobile: 'px-4',
      desktop: 'px-6',
    },
  },
});

type TabItemBaseProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: React.ReactNode;
  /**
   * Use the tab as a child element (renders as Slot component from Radix).
   * Useful for creating tab-styled links or other elements.
   */
  asChild?: boolean;
  /**
   * The unique identifier value of the tab. Must be set so that the tabs can find the active tab.
   */
  value: string;
  /**
   * Whether the tab is currently active/selected. Optional - by default the Tab takes active value from context.
   */
  active?: boolean;
};

type TabItemPrimaryProps = TabItemBaseProps & {
  /**
   * Visual variant of the tab item.
   */
  variant?: 'primary';
  /**
   * Icon is only available for the secondary variant.
   */
  icon?: never;
};

type TabItemSecondaryProps = TabItemBaseProps & {
  /**
   * Visual variant of the tab item.
   */
  variant: 'secondary';
  /**
   * Icon to display on the left side of the tab label.
   */
  icon?: SvgIcon;
};

export type TabItemProps = TabItemPrimaryProps | TabItemSecondaryProps;

export const TabItem = forwardRef<HTMLButtonElement, TabItemProps>(
  (
    {
      className,
      children,
      disabled,
      asChild,
      onClick,
      value,
      active,
      icon,
      variant: variantProp,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const {
      activeValue,
      animated,
      layoutId,
      onActiveValueChange,
      size,
      variant: contextVariant,
    } = useTabsContext();
    const variant = variantProp ?? contextVariant ?? 'primary';
    const isPrimary = variant === 'primary';
    const shouldAnimateIndicator = isPrimary && animated;

    // Check the prop first and if not provided, check the context
    const isActive = active !== undefined ? active : value === activeValue;

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onActiveValueChange?.(value);
        onClick?.(e);
      },
      [onActiveValueChange, value, onClick],
    );

    const tabContent = (
      <Comp
        className={cn(
          tabItemVariants({ variant }),
          variant === 'primary' && primaryTabItemVariants({ size }),
          className,
        )}
        data-state={isActive && 'active'}
        ref={ref}
        disabled={disabled}
        onClick={handleClick}
        {...props}>
        {!isPrimary && icon && <Icon icon={icon} className="size-[16px]" />}
        <Slottable>{children}</Slottable>
        {isPrimary && isActive && (
          <div className="absolute bottom-[-1px] z-10 w-full transition-none">
            {shouldAnimateIndicator ? (
              <m.div
                className="h-[2px] bg-indigo-crush"
                layoutId={layoutId}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                  mass: 0.8,
                }}
              />
            ) : (
              <div className="h-[2px] bg-indigo-crush" />
            )}
          </div>
        )}
      </Comp>
    );

    return shouldAnimateIndicator ? (
      <LazyMotion features={loadDomMax} strict>
        {tabContent}
      </LazyMotion>
    ) : (
      tabContent
    );
  },
);
TabItem.displayName = 'TabItem';
